import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Vote } from "lucide-react";
import { article36Api } from "../../services/article36Api";
import { EagleState } from "../../components/EagleState";
import { useLocalization } from "../../hooks/useLocalization";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";

export default function MyArticle36VotePage() {
  const { t } = useLocalization();
  const [elections, setElections] = useState(null);
  const [submitting, setSubmitting] = useState("");

  const load = useCallback(() => {
    article36Api.myElections().then((r) => setElections(r.data)).catch(() => setElections([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const vote = async (electionId, choice) => {
    setSubmitting(electionId);
    try {
      await article36Api.castVote(electionId, choice);
      toast.success(t("article36.voteRecorded"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting("");
    }
  };

  if (elections === null) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  return (
    <div data-testid="my-article36-vote-page" className="space-y-5">
      <PageHelpBanner pageKey="my-article36-vote" title="How to use this page">
        <p>If your workplace has an open Article 36 representative election, it appears here - vote For, Against, or Abstain. Your vote is confidential and can only be cast once per election.</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("article36.myVoteTitle")}</h2>
        <p className="text-sm text-gray-500">{t("article36.myVoteSubtitle")}</p>
      </div>

      {elections.length === 0 && (
        <EagleState variant="empty" title={t("article36.noOpenElections")} message={t("article36.myVoteSubtitle")} testId="my-article36-vote-empty" />
      )}

      {elections.map((el) => (
        <div key={el.id} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`my-article36-election-${el.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <Vote size={18} className="text-blue-600" />
            <div>
              <p className="text-xs text-gray-500">{t("article36.candidateLabel")}</p>
              <p className="text-sm font-semibold text-gray-800">{el.candidate_name}</p>
            </div>
          </div>
          {el.has_voted ? (
            <p className="text-sm text-emerald-600 font-semibold" data-testid={`my-article36-already-voted-${el.id}`}>{t("article36.alreadyVoted")}</p>
          ) : (
            <div className="flex gap-2">
              <button data-testid={`my-article36-vote-for-${el.id}`} disabled={submitting === el.id} onClick={() => vote(el.id, "FOR")} className="flex-1 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:scale-95 transition-all disabled:opacity-50">{t("article36.voteFor")}</button>
              <button data-testid={`my-article36-vote-against-${el.id}`} disabled={submitting === el.id} onClick={() => vote(el.id, "AGAINST")} className="flex-1 py-2 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 active:scale-95 transition-all disabled:opacity-50">{t("article36.voteAgainst")}</button>
              <button data-testid={`my-article36-vote-abstain-${el.id}`} disabled={submitting === el.id} onClick={() => vote(el.id, "ABSTAIN")} className="flex-1 py-2 rounded-full border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 active:scale-95 transition-all disabled:opacity-50">{t("article36.voteAbstain")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
